import { ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { GlobalUploadStatusService, GlobalUploadStatus } from '../../services/global-upload-status.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-global-upload-status',
  standalone: true,
  imports: [],
  template: `
    <!-- Simple inline status indicator for breadcrumb area -->
    @if (uploadStatus.isActive) {
      <div
        class="flex items-center gap-3 text-sm ml-8 cursor-pointer hover:opacity-75 transition-opacity"
        [class.text-primary]="uploadStatus.status === 'uploading'"
        [class.text-secondary]="uploadStatus.status === 'processing'"
        [class.text-success]="uploadStatus.status === 'completed'"
        [class.text-error]="uploadStatus.status === 'error'"
        (click)="handleStatusClick()"
        [title]="getClickHelpText()">
        <div class="loading loading-spinner loading-sm small-spinner"></div>
        <span>{{ getSimpleStatusText() }}</span>
        <span class="opacity-70">{{ getCurrentProgress().toFixed(0) }}%</span>
        <!-- Show dismiss button if stuck -->
        @if (isStuckUpload()) {
          <button
            class="btn btn-ghost btn-xs text-error hover:bg-error hover:text-error-content ml-2"
            (click)="clearStuckUpload(); $event.stopPropagation()"
            title="Clear stuck upload">
            ×
          </button>
        }
      </div>
    }
    `,
  styles: [`
    /* Custom smaller spinner (50% of loading-sm) */
    .small-spinner {
      width: 0.75rem !important;
      height: 0.75rem !important;
      border-width: 1px !important;
    }
  `]
})
export class GlobalUploadStatusComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  uploadStatus: GlobalUploadStatus = {
    isActive: false,
    type: 'video',
    status: 'idle', 
    progress: 0,
    processingProgress: 0,
    fileName: '',
    message: '',
    uploadId: '',
    startTime: new Date()
  };
  
  private subscription: Subscription = new Subscription();

  constructor(
    private globalUploadService: GlobalUploadStatusService,
    private router: Router,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.globalUploadService.uploadStatus$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(status => {
        this.uploadStatus = status;
        this.cdr.markForCheck();
      })
    );

    // Check if there's a resumed upload that needs completion
    const currentStatus = this.globalUploadService.getCurrentStatus();
    
    if (currentStatus.isActive && currentStatus.status === 'processing') {
      // Calculate how long the upload has been processing
      const processingTime = Date.now() - new Date(currentStatus.startTime).getTime();
      const processingMinutes = processingTime / (1000 * 60);
      
      
      // If it's been processing for more than 2 minutes, assume it's complete
      if (processingMinutes > 2) {
        this.globalUploadService.completeUpload(currentStatus.uploadId);
      } else if (currentStatus.videoId) {
        this.globalUploadService.resumeUploadChecking(
          currentStatus.uploadId,
          currentStatus.videoId,
          this.http,
          environment.apiUrl
        );
      } else {
        // No videoId stored, complete after a short delay
        setTimeout(() => {
          this.globalUploadService.completeUpload(currentStatus.uploadId);
        }, 10000); // 10 second timeout
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getStatusTitle(): string {
    switch (this.uploadStatus.status) {
      case 'uploading': return 'Uploading';
      case 'processing': return 'Processing';
      case 'completed': return 'Complete';
      case 'error': return 'Error';
      default: return 'Upload';
    }
  }

  getSimpleStatusText(): string {
    switch (this.uploadStatus.status) {
      case 'uploading': return 'Uploading...';
      case 'processing': return 'Processing...';
      case 'completed': return 'Complete';
      case 'error': return 'Upload failed';
      default: return 'Upload';
    }
  }

  getCurrentProgress(): number {
    if (this.uploadStatus.status === 'uploading') {
      return this.uploadStatus.progress;
    } else if (this.uploadStatus.status === 'processing') {
      return this.uploadStatus.processingProgress;
    } else if (this.uploadStatus.status === 'completed') {
      return 100;
    }
    return 0;
  }

  navigateBack(): void {
    if (this.uploadStatus.navigateBackRoute) {
      this.router.navigate([this.uploadStatus.navigateBackRoute]);
    }
  }

  hideWidget(): void {
    this.globalUploadService.hideUploadStatus(this.uploadStatus.uploadId);
  }

  handleRetry(): void {
    // For now, just hide the widget - retry logic would be handled by the original component
    this.hideWidget();
  }

  handleStatusClick(): void {
    
    // Always prioritize clearing stuck uploads
    if (this.isStuckUpload()) {
      this.clearStuckUpload();
      return;
    }
    
    // For uploads showing 100% for more than a few seconds, also allow clearing
    const minutesSinceStart = (Date.now() - this.uploadStatus.startTime.getTime()) / (1000 * 60);
    const isStuckAt100 = (this.uploadStatus.progress >= 100 || this.uploadStatus.processingProgress >= 100) && minutesSinceStart > 0.1;
    
    if (isStuckAt100) {
      this.clearStuckUpload();
      return;
    }
    
    // Only navigate if not stuck and explicitly allowed
    if (this.uploadStatus.canNavigateBack) {
      this.navigateBack();
    }
  }

  isStuckUpload(): boolean {
    return this.globalUploadService.isStuck();
  }

  clearStuckUpload(): void {
    this.globalUploadService.clearStuckStatus();
  }

  getClickHelpText(): string {
    // Check for stuck at 100% condition
    const minutesSinceStart = (Date.now() - this.uploadStatus.startTime.getTime()) / (1000 * 60);
    const isStuckAt100 = (this.uploadStatus.progress >= 100 || this.uploadStatus.processingProgress >= 100) && minutesSinceStart > 0.1;
    
    if (this.isStuckUpload() || isStuckAt100) {
      return 'Click to dismiss stuck upload';
    }
    if (this.uploadStatus.canNavigateBack) {
      return 'Click to return to upload page';
    }
    return 'Upload in progress - click to dismiss if stuck';
  }
}